import { Component } from '@theme/component';

/**
 * Subscription collection card: syncs button price when frequency changes.
 * The plan `<select>` uses `name="selling_plan"` + `form` attribute (refs inside
 * product-form-component are not visible to this parent component).
 *
 * @typedef {object} Refs
 * @property {HTMLSelectElement} planSelect
 *
 * @extends {Component<Refs>}
 */
class SubscriptionCollectionCardComponent extends Component {
  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abortController;

    this.addEventListener('submit', this.#handleFormSubmit, { capture: true, signal });
    this.#syncFromSelectedPlan();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#abortController = new AbortController();
  }

  handlePlanChange = () => {
    this.#syncFromSelectedPlan();
  };

  /**
   * @param {Event} event
   */
  #handleFormSubmit = (event) => {
    const form = this.querySelector('form');
    if (form && event.target === form) {
      this.#syncFromSelectedPlan();
    }
  };

  #syncFromSelectedPlan() {
    const { planSelect } = this.refs;
    if (!planSelect) return;

    const option = planSelect.selectedOptions[0];
    if (!option) return;

    const priceDisplay = this.querySelector('[ref="priceDisplay"]');
    if (priceDisplay) {
      const priceHtml = this.#buildPriceHtml(option);
      if (priceHtml) {
        priceDisplay.innerHTML = priceHtml;
      }
    }
  }

  /**
   * @param {HTMLOptionElement} option
   * @returns {string}
   */
  #buildPriceHtml(option) {
    const subscribeDisplay = option.dataset.subscribeDisplay;
    if (!subscribeDisplay) return '';

    const showCompare = option.dataset.showCompare === '1';
    const oneTimeDisplay = option.dataset.onetimeDisplay ?? '';
    const priceRegular = this.dataset.priceRegular ?? '';
    const priceSale = this.dataset.priceSale ?? '';

    const wrapperClass = showCompare
      ? 'add-to-cart-button-price add-to-cart-button-price--has-compare'
      : 'add-to-cart-button-price';

    let html = `<span class="${wrapperClass}">`;

    if (showCompare && oneTimeDisplay) {
      html += `<span class="visually-hidden">${priceRegular}&nbsp;</span>`;
      html += `<span class="compare-at-price">${oneTimeDisplay}</span>`;
      html += `<span class="visually-hidden">${priceSale}&nbsp;</span>`;
    }

    html += `<span class="price">${subscribeDisplay}</span></span>`;
    return html;
  }
}

if (!customElements.get('subscription-collection-card-component')) {
  customElements.define('subscription-collection-card-component', SubscriptionCollectionCardComponent);
}
